import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { StoryList } from '../components/StoryList';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { useNavigate } from 'react-router-dom';
import { ChangePasswordSection } from '../components/ChangePasswordSection';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ProfilePicture = styled.div<{ $imageUrl?: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: ${props => props.$imageUrl ? 'transparent' : '#e9ecef'};
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  margin-right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 600;
  color: #495057;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 16px;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const Username = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const Email = styled.p`
  font-size: 1rem;
  color: #868e96;
  margin: 0 0 16px 0;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  background-color: transparent;
  color: ${props => props.$active ? COLORS.PRIMARY : '#495057'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? COLORS.PRIMARY : 'transparent'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${COLORS.PRIMARY};
  }
`;

const ChildrenSection = styled.div`
  margin-bottom: 32px;
`;

const ChildrenList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ChildCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ChildHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ChildName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
`;

const ChildAge = styled.p`
  font-size: 0.875rem;
  color: #868e96;
  margin: 0 0 8px 0;
`;

const ChildForm = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const FormTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 16px 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

enum ProfileTab {
  STORIES = 'stories',
  CHILDREN = 'children',
  SETTINGS = 'settings',
}

export const ProfilePage: React.FC = () => {
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [activeTab, setActiveTab] = useState<ProfileTab>(ProfileTab.STORIES);
  const [showChildForm, setShowChildForm] = useState<boolean>(false);
  
  // Child form state
  const [childName, setChildName] = useState<string>('');
  const [childAge, setChildAge] = useState<string>('');
  const [childGender, setChildGender] = useState<string>('neutral');
  
  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: '/profile' } });
    }
  }, [isLoggedIn, navigate]);
  
  if (!isLoggedIn || !user) {
    return null;
  }
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  const handleAddChild = () => {
    // This would be implemented with a backend call
    // For now, just reset form and hide it
    setChildName('');
    setChildAge('');
    setChildGender('neutral');
    setShowChildForm(false);
  };
  
  const handleRemoveChild = (childId: string) => {
    // This would be implemented with a backend call
  };
  
  const formatAgeRange = (ageRange: string) => {
    switch (ageRange) {
      case '2-4':
        return 'Toddler (2-4 years)';
      case '4-6':
        return 'Preschool (4-6 years)';
      case '6-9':
        return 'School Age (6-9 years)';
      case '9-12':
        return 'Preteen (9-12 years)';
      default:
        return ageRange;
    }
  };
  
  return (
    <ProfileContainer>
      <ProfileHeader>
        {user.profilePicture ? (
          <ProfilePicture $imageUrl={user.profilePicture} />
        ) : (
          <ProfilePicture>{getInitials(user.username)}</ProfilePicture>
        )}
        
        <ProfileInfo>
          <Username>{user.username}</Username>
          <Email>{user.email}</Email>
          {/* todo: add edit profile functionality */}
          {/* <Button variant="outline" size="small">
            {t('profile.edit')}
          </Button> */}
        </ProfileInfo>
      </ProfileHeader>
      
      <TabContainer>
        <Tab 
          $active={activeTab === ProfileTab.STORIES} 
          onClick={() => setActiveTab(ProfileTab.STORIES)}
        >
          {t('profile.myStories')}
        </Tab>
        {/* TODO - add children to each profile */}
        {/* <Tab 
          active={activeTab === ProfileTab.CHILDREN} 
          onClick={() => setActiveTab(ProfileTab.CHILDREN)}
        >
          Children
        </Tab> */}
        <Tab 
          $active={activeTab === ProfileTab.SETTINGS} 
          onClick={() => setActiveTab(ProfileTab.SETTINGS)}
        >
          {t('profile.settings')}
        </Tab>
      </TabContainer>
      
      {activeTab === ProfileTab.STORIES && (
        <StoryList 
          title={t('profile.yourStories')} 
          userId={user.id} 
          showFilters={true}
        />
      )}
      
      {activeTab === ProfileTab.CHILDREN && (
        <ChildrenSection>
          {user.children && user.children.length > 0 ? (
            <ChildrenList>
              {user.children.map((child) => (
                <ChildCard key={child.id}>
                  <ChildHeader>
                    <ChildName>{child.name}</ChildName>
                    <Button 
                      variant="danger" 
                      size="small"
                      onClick={() => handleRemoveChild(child.id)}
                    >
                      Remove
                    </Button>
                  </ChildHeader>
                  <ChildAge>{formatAgeRange(child.age)}</ChildAge>
                </ChildCard>
              ))}
            </ChildrenList>
          ) : (
            <p>You haven't added any children yet. Add a child to personalize stories.</p>
          )}
          
          {showChildForm ? (
            <ChildForm>
              <FormTitle>Add Child</FormTitle>
              <FormRow>
                <Input
                  label="Child's Name"
                  placeholder="Enter name"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                />
                <Input
                  label="Age Range"
                  placeholder="e.g., 4-6"
                  value={childAge}
                  onChange={(e) => setChildAge(e.target.value)}
                />
              </FormRow>
              
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 500, color: '#343a40' }}>
                Gender (for story personalization)
              </label>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={childGender === 'boy'}
                    onChange={() => setChildGender('boy')}
                  />
                  Boy
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={childGender === 'girl'}
                    onChange={() => setChildGender('girl')}
                  />
                  Girl
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={childGender === 'neutral'}
                    onChange={() => setChildGender('neutral')}
                  />
                  Neutral
                </label>
              </div>
              
              <div style={{ display: 'flex', gap: '16px' }}>
                <Button 
                  variant="outline" 
                  onClick={() => setShowChildForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleAddChild}
                  disabled={!childName.trim() || !childAge.trim()}
                >
                  Add Child
                </Button>
              </div>
            </ChildForm>
          ) : (
            <Button 
              variant="primary" 
              onClick={() => setShowChildForm(true)}
            >
              Add Child
            </Button>
          )}
        </ChildrenSection>
      )}
      
      {activeTab === ProfileTab.SETTINGS && (
  <div>
    <h2>{t('profile.accountSettings')}</h2>
    
    {/* Show change password only for local auth users */}
    {user?.authProvider === 'local' && (
      <ChangePasswordSection />
    )}
    
    {/* Show account type info for Google users */}
    {user?.authProvider === 'google' && (
      <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '24px' }}>
        <h3>{t('profile.googleAccount')}</h3>
        <p>{t('profile.googleAccountInfo')}</p>
        <a 
          href="https://myaccount.google.com/security" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#4263eb', textDecoration: 'none' }}
        >
          {t('profile.googleAccountSecurity')}
        </a>
      </div>
    )}
    
    {/* Other settings that apply to all users */}
    {/* <NotificationSettings />
    <AccountPreferences /> */}
  </div>
)}
    </ProfileContainer>
  );
};